import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && isAuthenticated) {
      verifyPayment(sessionId);
    } else if (!sessionId) {
      navigate("/pricing");
    }
  }, [searchParams, isAuthenticated]);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setVerified(true);
        toast.success("Welcome to Subspace Pro!");
      } else {
        toast.error("Payment verification failed");
        setTimeout(() => navigate("/pricing"), 2000);
      }
    } catch (error) {
      console.error("Verification failed:", error);
      toast.error("Failed to verify payment");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full"
      >
        <GlassCard hover={false} className="text-center p-8">
          {verifying ? (
            <>
              <Loader2 className="animate-spin mx-auto mb-4 text-violet-500" size={48} />
              <h2 className="text-xl font-bold text-white mb-2">Verifying Payment...</h2>
              <p className="text-gray-400 text-sm">Please wait while we confirm your subscription</p>
            </>
          ) : verified ? (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to Pro!</h2>
              <p className="text-gray-400 mb-6">
                Your subscription is now active. Enjoy unlimited access to all features!
              </p>
              
              <div className="space-y-3">
                <NeonButton onClick={() => navigate("/studio")} className="w-full">
                  Start Exploring
                  <ArrowRight size={18} className="ml-2" />
                </NeonButton>
                <NeonButton variant="ghost" onClick={() => navigate("/")} className="w-full">
                  Go to Home
                </NeonButton>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white mb-2">Payment Issue</h2>
              <p className="text-gray-400 mb-4">
                We couldn't verify your payment. Please try again or contact support.
              </p>
              <NeonButton onClick={() => navigate("/pricing")} className="w-full">
                Back to Pricing
              </NeonButton>
            </>
          )}
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
